import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { formatBytes } from '../ui/bytes'
import { Icon } from '../ui/Icon'
import { getSharedFile, getSharedFiles, shareErrText } from '../transport/relay'

/**
 * THE AGENT'S FOLDER, when the share allows it — owner, 2026-10-17: 可以選擇讓好友看到 Agent working
 * folder 裡面的檔案 (預設看不到). Opened by the header's Files button, in the same drawer your own
 * agent's files open in (#fsDock) — read only: a folder at a time and a file at a time, through the
 * share's own routes, which the machine confines to that one folder.
 */
export function SharedFilesDock({ id, onClose }: { id: string; onClose: () => void }) {
  const [rel, setRel] = useState('')
  const [open, setOpen] = useState<string | null>(null)
  const list = useQuery({ queryKey: ['shared-files', id, rel], queryFn: () => getSharedFiles(id, rel) })
  const file = useQuery({
    queryKey: ['shared-file', id, open],
    queryFn: () => getSharedFile(id, open ?? ''),
    enabled: open !== null,
  })
  const folder = list.data?.folder || 'Folder'
  const where = open ?? rel
  const up = () => {
    if (open !== null) setOpen(null)
    else setRel(rel.split('/').slice(0, -1).join('/'))
  }
  const f = file.data
  return (
    <aside id="fsDock" className="shared-fs" aria-label="Shared files">
      <div className="fs-hd">
        <button
          type="button"
          className="fs-icon"
          title="Up"
          aria-label="Up"
          disabled={open === null && !rel}
          onClick={up}
        >
          <Icon name="arrow_upward" size={16} label="Up" />
        </button>
        <span className="fs-path" title={where ? `${folder}/${where}` : folder}>
          {where ? `${folder} / ${where.split('/').join(' / ')}` : folder}
        </span>
        <button type="button" className="fs-icon" title="Close" aria-label="Close" onClick={onClose}>
          <Icon name="close" size={16} label="Close" />
        </button>
      </div>
      {open === null ? (
        <div className="fs-list" role="list">
          {list.isPending && <div className="pm-empty">Loading…</div>}
          {list.isError && <div className="pm-empty sb-err">{shareErrText(list.error)}</div>}
          {list.data && list.data.entries.length === 0 && <div className="pm-empty">Empty folder.</div>}
          {list.data?.entries.map((e) => (
            <div className="fs-row" role="listitem" key={`${e.type}:${e.name}`}>
              <button
                type="button"
                className="fs-open"
                title={e.type === 'dir' ? 'Open folder' : e.name}
                onClick={() => {
                  const next = rel ? `${rel}/${e.name}` : e.name
                  if (e.type === 'dir') setRel(next)
                  else setOpen(next)
                }}
              >
                <Icon name={e.type === 'dir' ? 'folder' : 'attach_file'} size={15} />
                <span className="fs-name">{e.name}</span>
                {e.type === 'file' && <span className="fs-size">{formatBytes(e.size ?? null)}</span>}
              </button>
            </div>
          ))}
          {list.data?.truncated && <div className="pm-empty">Only the first 2000 are listed.</div>}
        </div>
      ) : (
        <div className="sas-viewer">
          {file.isPending && <div className="pm-empty">Loading…</div>}
          {file.isError && <div className="pm-empty sb-err">{shareErrText(file.error)}</div>}
          {f && !f.ok && (
            <div className="pm-empty">
              {f.error === 'too_large' ? `Too large to show here (${formatBytes(f.size ?? null)}).` : 'This file cannot be shown here.'}
            </div>
          )}
          {f?.ok && f.encoding === 'utf8' && (
            <>
              <pre className="sas-text">{f.data}</pre>
              {f.truncated && <div className="pm-empty">Only the beginning of this file is shown.</div>}
            </>
          )}
          {f?.ok && f.encoding === 'base64' && f.kind === 'image' && (
            <img className="sas-img" alt={f.name ?? ''} src={`data:${f.mime ?? 'image/png'};base64,${f.data ?? ''}`} />
          )}
          {f?.ok && f.encoding === 'base64' && f.kind !== 'image' && (
            <div className="pm-empty">This file cannot be shown here.</div>
          )}
        </div>
      )}
    </aside>
  )
}
